import React, {useMemo, useState} from 'react';
import {Button, ListGroup, Stack} from "react-bootstrap";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ButtonDelete} from "../../../_components/DeleteButton";
import {useAlert, useAuth} from "../../../../context";
import {ColumnizeText, OurClearAlert, SingleText} from "../../../_components";
import {useCarrier, useCountryCount, useInfiniteScroll} from "../../../../hooks";
import {useCountryList} from "../../../../hooks";
import {isoTimestampToReadableDate} from "../utilities/readableDate";
import {PageTitle} from "../../../_components";
import {DynamicBox, DynamicDialog} from "../../../_components/BoxGroupLayout";
import {Formik, Form as FormikForm, FieldArray} from "formik";
import {WhiteCard} from "../../../device/_components/WhiteCard";

export function AddEditCarrier() {
    const {caid} = useParams()
    const {userId} = useAuth()
    const {success} = useAlert()
    const navigate = useNavigate()

    const {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        errorValue,
        clearErrorValue,
    } = useCarrier(userId, caid)

    const edit = caid !== "add"

    function handleSubmit(values, actions) {
        if (edit) {
            editItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        } else {
            addItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        }
    }


    function handleOnDelete() {
        deleteItem()
            .then(msg => {
                success(msg)
                navigate(-1)
            })
            .catch(console.warn)
    }

    return (
        <>
            <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

            <PageTitle title="Carrier Details">{edit ? "Edit" : "Add"} carrier.</PageTitle>

            <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={value} enableReinitialize={true}>
                {({isSubmitting, resetForm}) => (
                    <FormikForm>
                        <WhiteCard>
                            <ColumnizeText>
                                <SingleText name="company" label="Company name"/>
                                <SingleText name="name" label="Service name"/>
                            </ColumnizeText>

                            <SingleText type="number" name="price" label="Shipment price" min={0} step=".01" prefix="€"/>

                            <SingleText name="tracking_url" label="Tracking URL"/>
                        </WhiteCard>

                        <WhiteCard>
                            <FieldArray name="countries" component={ShipTo}/>
                            <div className="small text-secondary">For Free Shipping add all the countries</div>
                        </WhiteCard>

                        <Stack className="my-2 text-secondary">
                            {value.created_at && <span>Created at: {isoTimestampToReadableDate(value.created_at)}</span>}
                            {value.updated_at && <span>Updated at: {isoTimestampToReadableDate(value.updated_at)}</span>}
                        </Stack>


                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                            <Button as={Link} variant="link" to={-1}>Go back</Button>
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            {edit && <ButtonDelete variant="danger" onDelete={handleOnDelete} value={value.name}>Delete</ButtonDelete>}
                            <Button type="submit" variant="success" disabled={isSubmitting}>Save</Button>
                        </Stack>
                    </FormikForm>
                )}
            </Formik>
        </>
    );
}


function ShipTo({push, remove, form}) {
    const [show, setShow] = useState(false)
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useCountryCount()
    const {list, loadingList} = useCountryList(pageSize, pageIndex, null, "name_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.countries.find(item2 => item2.id === item.id)), [list, form.values.countries])
    const renderList = useMemo(() => tempList.filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])
    const selectedItems = useMemo(() => form.values.countries.sort((a, b) => a.name.localeCompare(b.name)), [form.values.countries])

    const isInvalid = typeof form.errors.countries === "string"

    return (
        <>
            <DynamicBox label="Ship to" isInvalid={isInvalid} feedback={form.errors.countries} button="Country"
                        onClick={() => setShow(true)}>
                {selectedItems.map((item, index) => (
                    <Button key={index} className="mb-2" onClick={() => remove(index)} variant="light">{item.name} <i className="bi bi-x-lg"/></Button>
                ))}
            </DynamicBox>
            <DynamicDialog title="Select Country" show={show} onHide={() => setShow(false)} filter={filter} setFilter={setFilter}>
                <div>Loaded {list.length - selectedItems.length} of {count - selectedItems.length}</div>
                {renderList && renderList.length > 0 ?
                    renderList.map((item, index) => (
                        <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                            <Button variant="light" onClick={() => push(list.find(c => c.id === item.id))} className="mb-2">{item.name}</Button>
                        </ListGroup.Item>
                    ))
                    :
                    <span className="text-center">No country found</span>
                }
            </DynamicDialog>
        </>
    )
}

